import * as React from 'react';
import { ProductPrice, SimpleProduct, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { ICoreContext, getCatalogId } from '@msdyn365-commerce/core';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

async function getPricesAsync(context: ICoreContext, products: number[], batchSize: number = 30): Promise<ProductPrice[]> {
    const result: ProductPrice[] = [];
    const catalogId = context.request.apiSettings.catalogId ?? getCatalogId(context.actionContext.requestContext);
    const projectDomain: ProjectionDomain = { ChannelId: +context.request.apiSettings.channelId, CatalogId: catalogId };

    // Work with X products at a time
    const batchCount = Math.ceil(products.length / batchSize);

    for (let i = 0; i < batchCount; i++) {
        const batch = products.slice(i * batchSize, (i + 1) * batchSize);
        const prices = await getActivePricesAsync({ callerContext: context.actionContext }, projectDomain, batch, new Date(), null, undefined, true);
        result.push(...prices);
    }

    return result;
}

export function useActivePrices(context: ICoreContext, products: SimpleProduct[] | number[] | undefined, batchSize: number = 30): [ProductPrice[], boolean, Error | undefined] {
    const [activePrices, setActivePrices] = React.useState<ProductPrice[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<Error | undefined>(undefined);

    // Build a stable, sorted, de-duplicated list of ids and a stable key
    const ids = React.useMemo<number[] | null>(() => {
        if (!products || products.length === 0) {
            return null;
        }
        const raw = typeof products[0] === 'number'
            ? (products as number[])
            : (products as SimpleProduct[]).map(p => p.RecordId);
        // sort + dedupe -> stable
        const uniqSorted = Array.from(new Set(raw)).filter(Boolean).sort((a, b) => a - b);
        return uniqSorted;
    }, [products]);

    const idsKey = React.useMemo(() => (ids ? ids.join('|') : ''), [ids]);

    React.useEffect(() => {
        if (!ids || ids.length === 0 || error || !products || products.length < 1 || !context.request.user.isAuthenticated) {
            setLoading(false);
            return;
        }

        if (ids.every(id => activePrices.some(price => price.ProductId === id))) {
            // We already have all the prices
            setLoading(false);
            return;
        }

        getPricesAsync(context, ids, batchSize)
            .then(prices => setActivePrices(prices))
            .catch(error => setError(error))
            .finally(() => setLoading(false));
    }, [idsKey]);

    return [activePrices, loading, error];
}
